import type { ViewQueryNode, ViewQueryRecordNode } from '@salescore/core'
import { Result } from 'effect'

import type { AddNewChildRecordLeft } from '../action/addNewRecord/addNewChildRecord'
import type { DeepReadonly } from '../types/DeepReadonly'
import { getChildRecordsWithParent } from './getChildRecordsWithParent'

/**
 * `innerRowIndex` の位置に新規行を挿入するときの、`recordNodes` 配列内の挿入先 index を返す。
 * `innerRowIndex` とちょうど同じ位置から始まる既存行がある場合は、その行の手前に挿入する (lower bound)。
 * 挿入位置以降の既存行はこの index を起点に押し下げられる。
 */
export const findIndexToInsertAt = (
  innerRowIndex: number,
  recordNodes: DeepReadonly<ViewQueryRecordNode[]>,
): number => {
  const insertIndex = recordNodes.findIndex((recordNode) => innerRowIndex <= recordNode.meta.innerRowIndexStart)
  return insertIndex === -1 ? recordNodes.length : insertIndex
}

/**
 * `innerRowIndex` の位置を占める既存行の直後に新規行を挿入するときの、`innerRows` 配列内の挿入先 index を返す。
 * `innerRowIndex` とちょうど同じ位置から始まる既存行がある場合は、その行の後ろに挿入する (upper bound)。
 */
export const findIndexToInsertAfter = (innerRowIndex: number, innerRows: Array<ViewQueryRecordNode['meta']>): number =>
  innerRows.findLastIndex((innerRow) => innerRow.innerRowIndexStart <= innerRowIndex) + 1

/**
 * `findIndexToInsertAfter` を内部で実行し、`recordNodes` の配列 index から、
 * 実際の挿入位置の `innerRowIndex` 値に変換して返す。
 */
export const computeChildInsertInnerRowIndex = (
  rootRecordNode: DeepReadonly<ViewQueryRecordNode>,
  innerRowIndex: number,
  node: DeepReadonly<ViewQueryNode>,
): Result.Result<number, AddNewChildRecordLeft> => {
  const result = getChildRecordsWithParent(rootRecordNode, { innerRowIndex, node })
  if (Result.isFailure(result)) {
    return Result.fail(result.failure)
  }

  const { recordNodes } = result.success
  const insertIndex = findIndexToInsertAfter(
    innerRowIndex,
    recordNodes.map((recordNode) => recordNode.meta),
  )

  return Result.succeed(
    insertIndex === 0 ? Math.max(0, innerRowIndex) : (recordNodes[insertIndex - 1]?.meta.innerRowIndexEnd ?? 0),
  )
}
